/**
 * Scoreboard rows for a manually counted asset type (AST-117).
 *
 * The Inventory scoreboard's "MAT (MANUAL)" row expands into one row per
 * MANUAL ASSET with counts (ST124 · 1200), instead of one per subtype.
 * Counts that aren't tied to a manual asset -- a subtype with no manual
 * assets, or entries saved before AST-118 -- stay as a SUBTYPE row in that
 * subtype's position (Laminate Mat · 62). Pure, so the ordering rules are
 * tested without rendering.
 *
 * Inputs
 *   entries       count entries ({ matTypeId, manualAssetId?, quantity, from/to })
 *   subtypes      the type's subtypes, in today's order [{ id, name }]
 *   manualAssets  [{ id, name, description, subtypeId, archived }]
 *   delta(entry)  +1 / -1 / 0 -- the project-total rule (utils/locationCounts)
 *
 * Returns { total, rows: [{ key, kind: 'asset'|'subtype', label, name,
 *   subtypeName, description, archived, count }] }
 *
 * Rules
 *   SB-3  one row per manual asset with counts
 *   SB-4  grouped by subtype in today's subtype order, then asset name A->Z
 *         (natural, so ST84 < ST124); a subtype's own unassigned row first
 *   SB-5  unassigned counts keep a subtype row in that subtype's position
 *   SB-7  rows netting to 0 are hidden; archived assets with counts get
 *         "(archived)" after the name
 *   SB-8  total = sum of the rows (hidden rows are 0, so this holds)
 *   SB-9  keyed by id, never by name: a rename changes the label only, and a
 *         manual asset that happens to share a subtype's name can't merge
 *         into that subtype's row
 */

const natural = (a, b) => a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' });

export const UNKNOWN_SUBTYPE = '__unknown__';

export function scoreboardRows(entries, subtypes, manualAssets, delta) {
  const assetById = new Map((manualAssets || []).map((a) => [a.id, a]));
  const subtypeOrder = new Map((subtypes || []).map((s, i) => [s.id, i]));
  const subtypeName = new Map((subtypes || []).map((s) => [s.id, s.name]));

  // key -> { kind, id, subtypeId, count }
  const tally = new Map();
  const bump = (key, init, n) => {
    if (!tally.has(key)) tally.set(key, { ...init, count: 0 });
    tally.get(key).count += n;
  };

  let total = 0;
  for (const e of entries || []) {
    const dir = delta(e);
    if (dir === 0) continue;
    const n = (Number(e.quantity) || 0) * dir;
    total += n;

    const asset = e.manualAssetId ? assetById.get(e.manualAssetId) : null;
    if (asset) {
      bump(`a:${asset.id}`, { kind: 'asset', id: asset.id, subtypeId: asset.subtypeId }, n);
    } else {
      // Unassigned -- or an asset id we can't resolve (names not loaded yet,
      // or a deleted record): count it under its subtype so no mats vanish.
      const sid = subtypeName.has(e.matTypeId) ? e.matTypeId : UNKNOWN_SUBTYPE;
      bump(`s:${sid}`, { kind: 'subtype', id: sid, subtypeId: sid }, n);
    }
  }

  const rows = [...tally.entries()]
    .filter(([, r]) => r.count !== 0) // SB-7
    .map(([key, r]) => {
      const sName = r.subtypeId === UNKNOWN_SUBTYPE ? 'Unknown type' : (subtypeName.get(r.subtypeId) || 'Unknown type');
      if (r.kind === 'subtype') {
        return { key, kind: 'subtype', label: sName, name: sName, subtypeId: r.subtypeId, subtypeName: sName, description: '', archived: false, count: r.count };
      }
      const a = assetById.get(r.id);
      return {
        key,
        kind: 'asset',
        label: a.archived ? `${a.name} (archived)` : a.name,
        name: a.name,
        subtypeId: r.subtypeId,
        subtypeName: sName,
        description: a.description || '',
        archived: !!a.archived,
        count: r.count,
      };
    })
    .sort((x, y) => {
      // SB-4: subtype order first; an unknown/foreign subtype sorts last.
      const ox = subtypeOrder.has(x.subtypeId) ? subtypeOrder.get(x.subtypeId) : Number.MAX_SAFE_INTEGER;
      const oy = subtypeOrder.has(y.subtypeId) ? subtypeOrder.get(y.subtypeId) : Number.MAX_SAFE_INTEGER;
      if (ox !== oy) return ox - oy;
      // Within a subtype: its own unassigned row first, then assets A->Z.
      if (x.kind !== y.kind) return x.kind === 'subtype' ? -1 : 1;
      return natural(x.name, y.name);
    });

  return { total, rows };
}

/** SB-6: "ST124 · Timber Mat", then the description on its own line. */
export function rowTooltip(row) {
  if (row.kind === 'subtype') return null;
  return { title: `${row.name} · ${row.subtypeName}`, description: row.description };
}
